import React, { useState, useEffect } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCar } from "@fortawesome/free-solid-svg-icons";
import { ScrollNavbar } from "../../components";
import { Price } from "../../services/searchService";
import { useAuth } from "../../contexts/AuthContext";
import Swal from "sweetalert2";
import {
  bookingService,
  CreateBookingPayload,
} from "../../services/bookingService";
import { paymentService } from "../../services/paymentService";
import config from "../../config/config";
import searchService from "../../services/searchService";
import { VERTICALS } from "../../constants/tripTypes";
import { CAB_TYPES } from "../../constants/vehicleTypes";
import { FuelType } from "../../constants/fuelTypes";
import { CUSTOMER_TYPES } from "../../constants/customerTypes";
import { PAYMENT_MODES } from "../../constants/paymentTypes";
import { BOOKING_STATUS } from "../../constants/bookingStatus";
import { calculateFare, calculateMultiCityFare } from "../../utils/fareCalculator";
import { getCarDisplayName, getFuelTypeDisplay } from "../../utils/vehicleDisplay";

// --- Helper Functions --- //

const formatPrice = (priceValue: number) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(priceValue);
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Standard 15-character GSTIN format (2-digit state code, 10-char PAN, entity
// code, default "Z", checksum).
const GST_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;

const CabDetails = () => {
  const [price, setPrice] = useState<Price | null>(null);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Traveller details default to the logged-in account's info, but accounts
  // created via OTP-only signup (see fusion_backend's otpController.js) get
  // a placeholder name ("Guest <phone>") and often no email at all, so these
  // fields must always be explicitly filled in and validated before
  // checkout - not just trusted from the account.
  const [sameAsAccount, setSameAsAccount] = useState(true);
  const [travellerName, setTravellerName] = useState("");
  const [travellerEmail, setTravellerEmail] = useState("");

  const [wantsGstInvoice, setWantsGstInvoice] = useState(false);
  const [gstNumber, setGstNumber] = useState("");

  const location = useLocation();
  const history = useHistory();
  const { id: priceId } = useParams<{ id: string }>();
  const { user } = useAuth();

  useEffect(() => {
    if (sameAsAccount && user) {
      setTravellerName(user.name || "");
      setTravellerEmail(user.email || "");
    }
  }, [user, sameAsAccount]);

  const handleSameAsAccountChange = (checked: boolean) => {
    setSameAsAccount(checked);
    if (checked && user) {
      setTravellerName(user.name || "");
      setTravellerEmail(user.email || "");
    }
  };

  const { searchParams } = (location.state as { searchParams: any }) || {};

  useEffect(() => {
    if (!priceId) {
      setError("No price ID provided.");
      setIsLoading(false);
      return;
    }

    const fetchPriceDetails = async () => {
      try {
        setIsLoading(true);
        const response = await searchService.getPriceById(priceId);
        if (response.success && response.data) {
          setPrice(response.data);
        } else {
          throw new Error(response.message || "Could not find cab details.");
        }
      } catch (err: any) {
        setError(err.message);
        Swal.fire("Error", err.message, "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPriceDetails();
  }, [priceId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading cab details...
      </div>
    );
  }

  if (error || !price) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <p className="text-red-500 text-xl mb-4">
          {error || "Could not load cab details."}
        </p>
        <button
          onClick={() => history.push("/dashboard")}
          className="bg-blue-600 text-white py-2 px-4 rounded"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!searchParams) {
    // Only check for searchParams, not for user
    history.replace("/");
    return null;
  }

  const getCarName = () => getCarDisplayName(price);

  const fare =
    searchParams.fareLegs && searchParams.fareLegs.length > 0
      ? calculateMultiCityFare(
          price,
          searchParams.fareLegs,
          searchParams.pickupTime,
          searchParams.sourceState,
          searchParams.departureDate,
          searchParams.returnDate
        )
      : calculateFare(
          price,
          searchParams.distanceKm,
          searchParams.pickupTime,
          searchParams.sourceState,
          searchParams.departureDate,
          searchParams.returnDate
        );

  const handlePayment = async () => {
    // 0. Check for authentication
    if (!user) {
      // Save the current path and search state to redirect back after login
      localStorage.setItem(
        "redirect_path",
        location.pathname + location.search
      );
      if (location.state) {
        localStorage.setItem("redirect_state", JSON.stringify(location.state));
      }
      Swal.fire({
        title: "Login Required",
        text: "You need to be logged in to book a cab.",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Go to Login",
      }).then((result) => {
        if (result.isConfirmed) {
          history.push("/login");
        }
      });
      return;
    }

    // Traveller details are always required, not just for account holders -
    // OTP-only accounts default to a "Guest <phone>" placeholder name and
    // often have no email on file at all.
    if (!travellerName.trim() || !travellerEmail.trim()) {
      Swal.fire(
        "Traveller Details Required",
        "Please provide the traveller's name and email to continue.",
        "warning"
      );
      return;
    }
    if (!EMAIL_REGEX.test(travellerEmail.trim())) {
      Swal.fire("Invalid Email", "Please enter a valid email address.", "warning");
      return;
    }

    if (wantsGstInvoice) {
      const normalizedGst = gstNumber.trim().toUpperCase();
      if (!normalizedGst) {
        Swal.fire(
          "GST Number Required",
          "Please enter a GST number, or uncheck the GST invoice option.",
          "warning"
        );
        return;
      }
      if (!GST_REGEX.test(normalizedGst)) {
        Swal.fire(
          "Invalid GST Number",
          "Please enter a valid 15-character GST number.",
          "warning"
        );
        return;
      }
    }

    // 1. Create the booking payload
    const bookingPayload: CreateBookingPayload = {
      booking_vertical: searchParams.vertical ?? VERTICALS.INTERCITY,
      booking_trip_type: searchParams.tripType,
      customer_type: CUSTOMER_TYPES.B2C,
      cab_type: CAB_TYPES.SEDAN, // This should be dynamic based on car details
      cab_model: price.vehicle_model,
      cab_fuel_type: price.fuel_type as FuelType,
      pickup_date: searchParams.departureDate,
      pickup_time: searchParams.pickupTime,
      pickup_address: searchParams.sourceAddress ?? searchParams.sourceCity,
      return_date: searchParams.returnDate,
      return_time: searchParams.dropTime,
      drop_address:
        searchParams.destinationAddress ?? searchParams.destinationCity,
      // For Airport, Plan.source is always the airport city (for both
      // Pickup and Drop-off) and Plan.destination is always null - neither
      // reliably represents "the real pickup location"/"the real drop
      // location" for a specific booking, so use the City actually resolved
      // on the search form instead. For every other vertical, the matched
      // Plan's own source/destination are the real route endpoints.
      source:
        searchParams.vertical === VERTICALS.AIRPORT
          ? searchParams.sourceCityId
          : price.Plan.source,
      destination:
        searchParams.vertical === VERTICALS.AIRPORT
          ? searchParams.destinationCityId
          : price.Plan.destination,
      // Per-km rate actually used for fare.baseFare - night_base_fare
      // replaces fixed_fare entirely during the night window (see
      // fareCalculator's computeCorePricing), so this must match whichever
      // one was used or trip-completion billing will re-derive a different
      // base fare than what was quoted.
      basefare_bt: fare.isNight ? price.night_base_fare : price.fixed_fare,
      basekms_bt: fare.billedKm,
      driverallowances_bt: fare.driverCharges,
      drivernightallowances_bt: fare.nightCharges,
      estimated_fare_bt: fare.totalFare,
      extrakm_rate: price.extra_fare,
      extrahr_rate: price.extra_hour_fare,
      waiting_rate: price.Plan.is_waiting ? price.Plan.waiting_charges : 0,
      free_waiting_minutes: price.free_waiting_minutes,
      tollcharges_bt: fare.tollCharges,
      permitcharges: 0,
      parkingcharges: fare.parkingCharges,
      discount_amount: 0,
      total_fare_before_tax: fare.totalFareBeforeTax,
      sgst_bt: fare.sgst,
      cgst_bt: fare.cgst,
      igst_bt: fare.igst,
      payment_mode: PAYMENT_MODES.ONLINE,
      booking_status: BOOKING_STATUS.PENDING,
      // Multi-City itinerary - source/destination above stay the real trip
      // endpoints; stops are additional waypoints (shown as "detours" in the
      // driver app). Empty/undefined for every other trip type.
      stops: searchParams.stops,
      // The rounded, customer-facing "included" km (not the raw route
      // distance, and not the pricing floor billedKm) - this is what's later
      // used as the extra-km floor at trip completion (see fusion_backend's
      // calculateBookingCompletionFare), matching what the customer was
      // actually quoted.
      distance_km: fare.includedKm,
      traveller_name: travellerName.trim(),
      traveller_email: travellerEmail.trim(),
      gst_claim: wantsGstInvoice,
      ...(wantsGstInvoice && { gst_number: gstNumber.trim().toUpperCase() }),
    };

    try {
      // 2. Create the booking in your backend
      const bookingRes = await bookingService.createBooking(
        user.id.toString(),
        bookingPayload
      );
      if (!bookingRes.success || !bookingRes.data) {
        throw new Error(bookingRes.message || "Failed to create booking.");
      }

      const bookingId = bookingRes.data.booking_id;

      // 3. Create a payment order with your backend
      const orderRes = await paymentService.createOrder({
        booking_id: bookingId,
        customer_email: user.email,
        customer_phone: user.phone,
      });
      if (!orderRes.success || !orderRes.data) {
        throw new Error(orderRes.message || "Failed to create payment order.");
      }

      const { order_id, amount, currency, key_id, payment_id } = orderRes.data;

      // 4. Open Razorpay Checkout
      const options = {
        key: key_id,
        amount,
        currency,
        name: "FusionCab",
        description: `Booking for ${getCarName()}`,
        order_id,
        handler: async (response: any) => {
          // 5. Verify the payment
          const verifyPayload = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            payment_id,
            booking_id: bookingId,
          };

          try {
            const verifyRes = await paymentService.verifyPayment(verifyPayload);
            if (verifyRes.success) {
              Swal.fire(
                "Payment Successful!",
                "Your booking is confirmed.",
                "success"
              );
              // Redirect to booking details page instead of dashboard
              history.push(`/bookings/${bookingId}`);
            } else {
              throw new Error(
                verifyRes.message || "Payment verification failed."
              );
            }
          } catch (err) {
            Swal.fire("Verification Failed", (err as Error).message, "error");
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.phone,
        },
        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (error) {
      Swal.fire("Booking Failed", (error as Error).message, "error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <ScrollNavbar />
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => history.goBack()}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium mb-6"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left side - Cab Details */}
          <div className="lg:col-span-2 bg-white p-8 rounded-lg shadow-md">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
              {getCarName()}
            </h1>
            <p className="text-md text-gray-500 mb-6">
              or similar {getFuelTypeDisplay(price.fuel_type)} vehicle
            </p>

            <div className="mb-8">
              {price.image && !imageError ? (
                <img
                  src={price.image}
                  alt={getCarName()}
                  className="w-full h-auto object-cover rounded-lg"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-full h-64 flex items-center justify-center rounded-lg bg-gray-100 text-gray-400">
                  <FontAwesomeIcon icon={faCar} className="text-6xl" />
                </div>
              )}
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Trip Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700 mb-8">
              <p>
                <strong>From:</strong>{" "}
                {searchParams.sourceAddress ?? searchParams.sourceCity}
              </p>
              <p>
                <strong>To:</strong>{" "}
                {searchParams.destinationAddress ?? searchParams.destinationCity}
              </p>
              <p>
                <strong>Pickup Date:</strong>{" "}
                {new Date(searchParams.departureDate).toLocaleDateString()}
              </p>
              <p>
                <strong>Pickup Time:</strong> {searchParams.pickupTime}
              </p>
              {searchParams.returnDate && (
                <p>
                  <strong>Return Date:</strong>{" "}
                  {new Date(searchParams.returnDate).toLocaleDateString()}
                </p>
              )}
              {searchParams.dropTime && (
                <p>
                  <strong>Return Time:</strong> {searchParams.dropTime}
                </p>
              )}
            </div>

            {searchParams.stops && searchParams.stops.length > 0 && (
              <>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Stops
                </h2>
                {/* Source -> stops -> final destination only - the return
                    leg back to source is a pricing input only, never shown
                    here (its cost is already folded into Total Amount below). */}
                <ol className="list-decimal list-inside text-gray-700 mb-8 space-y-1">
                  {searchParams.stops.map((stop: any, index: number) => (
                    <li key={index}>{stop.address}</li>
                  ))}
                </ol>
              </>
            )}

            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Inclusions & Exclusions
            </h2>
            <div className="space-y-4">
              <ul className="list-disc list-inside text-green-600">
                {fare.includedKm > 0 && (
                  <li>{fare.includedKm} KMs included</li>
                )}
                {price.Plan.is_driver && <li>Driver allowance</li>}
                {fare.nightCharges > 0 && <li>Night charges included</li>}
                {price.Plan.is_toll && <li>Toll charges included</li>}
                {price.Plan.is_tax && <li>State tax & GST included</li>}
              </ul>
              <ul className="list-disc list-inside text-red-600">
                <li>Extra KMs at {formatPrice(price.extra_fare)}/KM</li>
                {searchParams.vertical === VERTICALS.HOURLY &&
                  price.extra_hour_fare > 0 && (
                    <li>
                      Extra hours at {formatPrice(price.extra_hour_fare)}/hr
                    </li>
                  )}
                {price.Plan.is_waiting && <li>Waiting charges may apply</li>}
                {price.Plan.is_parking && <li>Parking charges may apply</li>}
                {price.Plan.is_night && fare.nightCharges === 0 && (
                  <li>Night charges may apply</li>
                )}
              </ul>
            </div>
          </div>

          {/* Right side - Payment Panel */}
          <div className="bg-white p-8 rounded-lg shadow-md h-fit">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Fare Details
            </h2>

            <div className="space-y-3 mb-6">
              <div className="text-sm text-gray-500">
                Trip Distance: {fare.distanceKm.toFixed(1)} km
                {fare.isNight && " • Night pickup"}
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Base Fare</span>
                <span>{formatPrice(fare.baseFare)}</span>
              </div>
              {fare.extraFare > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Extra Distance ({fare.extraKm.toFixed(1)} km)</span>
                  <span>{formatPrice(fare.extraFare)}</span>
                </div>
              )}
              {fare.tollCharges > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Toll Charges</span>
                  <span>{formatPrice(fare.tollCharges)}</span>
                </div>
              )}
              {fare.parkingCharges > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Parking Charges</span>
                  <span>{formatPrice(fare.parkingCharges)}</span>
                </div>
              )}
              {fare.driverCharges > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Driver Allowance</span>
                  <span>{formatPrice(fare.driverCharges)}</span>
                </div>
              )}
              {fare.waitingCharges > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Waiting Charges</span>
                  <span>{formatPrice(fare.waitingCharges)}</span>
                </div>
              )}
              {(fare.stateTax > 0 || fare.greenTax > 0) && (
                <div className="flex justify-between text-gray-700">
                  <span>State/Green Tax</span>
                  <span>{formatPrice(fare.stateTax + fare.greenTax)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-700">
                <span>
                  {fare.sgst > 0 || fare.cgst > 0 ? "SGST + CGST (5%)" : "IGST (5%)"}
                </span>
                <span>{formatPrice(fare.sgst + fare.cgst + fare.igst)}</span>
              </div>
              <div className="border-t my-2"></div>
              <div className="flex justify-between text-xl font-bold text-gray-900">
                <span>Total Amount</span>
                <span>{formatPrice(fare.totalFare)}</span>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-md font-semibold text-gray-800 mb-3">
                Traveller Details
              </h3>
              <label className="flex items-center gap-2 mb-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={sameAsAccount}
                  onChange={(e) => handleSameAsAccountChange(e.target.checked)}
                />
                Same as my account
              </label>
              <div className="space-y-3">
                <div>
                  <label
                    htmlFor="traveller_name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Traveller Name
                  </label>
                  <input
                    type="text"
                    id="traveller_name"
                    value={travellerName}
                    onChange={(e) => setTravellerName(e.target.value)}
                    disabled={sameAsAccount}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="Enter traveller's name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="traveller_email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Traveller Email
                  </label>
                  <input
                    type="email"
                    id="traveller_email"
                    value={travellerEmail}
                    onChange={(e) => setTravellerEmail(e.target.value)}
                    disabled={sameAsAccount}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    placeholder="Enter traveller's email"
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="flex items-center gap-2 mb-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={wantsGstInvoice}
                  onChange={(e) => setWantsGstInvoice(e.target.checked)}
                />
                I need a GST invoice
              </label>
              {wantsGstInvoice && (
                <div>
                  <label
                    htmlFor="gst_number"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    GST Number
                  </label>
                  <input
                    type="text"
                    id="gst_number"
                    value={gstNumber}
                    onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. 22AAAAA0000A1Z5"
                    maxLength={15}
                  />
                </div>
              )}
            </div>

            <div className="mb-6">
              <label
                htmlFor="coupon"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Coupon Code
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="coupon"
                  className="flex-grow border-gray-300 rounded-l-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter coupon"
                />
                <button className="bg-gray-800 text-white px-4 rounded-r-md hover:bg-black">
                  Apply
                </button>
              </div>
            </div>

            <button
              onClick={handlePayment}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors"
            >
              Make Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CabDetails;
